import { useEffect, useMemo, useRef, useState } from "react";

import type { DecryptedItem } from "../auth";
import { Identicon } from "../components/Identicon";

interface Props {
  items: DecryptedItem[];
  onOpen: (item: DecryptedItem) => void;
  onAdd: () => void;
  onLock: () => void;
  onSync: () => Promise<void>;
  onSettings: () => void;
}

function hostFromUrl(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

export function List({ items, onOpen, onAdd, onLock, onSync, onSettings }: Props) {
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => {
      const d = it.data;
      return (
        d.name.toLowerCase().includes(needle) ||
        d.username.toLowerCase().includes(needle) ||
        d.url.toLowerCase().includes(needle)
      );
    });
  }, [items, q]);

  async function handleSync() {
    setSyncing(true);
    try {
      await onSync();
    } finally {
      setSyncing(false);
    }
  }

  return (
    <>
      <div className="header">
        <h1>Sejf</h1>
        <button className="back-btn" onClick={handleSync} disabled={syncing} aria-label="Synchronizuj">
          {syncing ? "…" : "↻"}
        </button>
        <button className="back-btn" onClick={onSettings} aria-label="Ustawienia">
          ⚙
        </button>
        <button className="back-btn" onClick={onLock} aria-label="Zablokuj">
          🔒
        </button>
      </div>

      <div className="main">
        <div className="search-bar">
          <span className="icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </span>
          <input
            ref={inputRef}
            type="search"
            placeholder="Szukaj — wpisz nazwę, login, domenę…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            enterKeyHint="search"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="empty">
            {items.length === 0 ? "Sejf jest pusty — dotknij + na dole, żeby dodać." : "Brak wyników"}
          </div>
        ) : (
          <div className="item-list">
            {filtered.map((it) => (
              <Row key={it.id} item={it} onClick={() => onOpen(it)} />
            ))}
          </div>
        )}
      </div>

      <div className="fab-bar">
        <button className="btn-block" onClick={onAdd} aria-label="Dodaj wpis">
          + Nowy wpis
        </button>
      </div>
    </>
  );
}

function Row({ item, onClick }: { item: DecryptedItem; onClick: () => void }) {
  const host = hostFromUrl(item.data.url);
  const seed = host || item.data.name || item.data.username || "?";
  const title = item.data.name || host || "(bez nazwy)";
  const subtitle = item.data.username || host;
  return (
    <div className="item-row" onClick={onClick}>
      <Identicon seed={seed} size={40} />
      <div className="item-row-text">
        <div className="item-row-title">{title}</div>
        <div className="item-row-sub">{subtitle}</div>
      </div>
    </div>
  );
}
